import { twMerge } from '@shared/themes';
import type { ComponentProps } from 'react';

type LotteryNumberVariant = 'text' | 'filled';

type LotteryNumberProps = ComponentProps<'span'> & {
  isMatched?: boolean;
  isHighlighted?: boolean;
  /**
   * How a match is shown.
   *
   * - `text` tints the numeral and adds a dot beneath it — the results-page treatment.
   * - `filled` seats every numeral in a 28px circle and fills the matched ones with the lottery
   *   accent, matching the live-draw board so a match reads the same in both places. Winning a
   *   prize is a separate thing from matching here, so `isHighlighted` does not change the fill —
   *   a match on a slip that won nothing still shows as a match.
   */
  variant?: LotteryNumberVariant;
};

/** `width/w-7` in the design; every numeral takes the same box so the row stays evenly spaced. */
const FILLED_BASE_CLASSES = 'inline-flex size-7 shrink-0 items-center justify-center rounded-full';

/**
 * The numeral stays near-black in both themes because the fill is a bright lime either way —
 * the theme's own foreground would be unreadable on it in dark mode.
 */
const FILLED_MATCH_CLASSES = 'bg-lottery-accent text-lottery-foreground';

/** A dot under a matched numeral, so matching is not conveyed by colour alone. */
const MATCH_DOT_CLASSES = 'after:absolute after:bg-current after:bottom-[-2px] after:left-1/2 after:-translate-x-1/2 after:size-[2.8px] after:rounded-full';

function resolveTextClasses(isMatched: boolean | undefined, isHighlighted: boolean | undefined): string {
  if (isMatched === undefined) return '';
  if (!isMatched) return 'text-foreground-subtle';
  return isHighlighted ? 'text-success-foreground' : 'text-foreground';
}

/**
 * A lottery number with consistent styling across the results surfaces.
 *
 * Matching states: matched and highlighted, matched without highlight, or unmatched. See
 * {@link LotteryNumberProps.variant} for how each is drawn.
 */
function LotteryNumber({ className, children, isMatched, isHighlighted, variant = 'text', ...props }: LotteryNumberProps) {
  const isFilled = variant === 'filled';
  const isFilledMatch = isFilled && isMatched === true;

  return (
    <span
      className={twMerge(
        'relative paragraph-l-strong',
        isFilled ? FILLED_BASE_CLASSES : undefined,
        // Filled leaves unmatched numerals at full contrast, per the design; only the circle
        // distinguishes a match.
        isFilled ? (isFilledMatch ? FILLED_MATCH_CLASSES : 'text-foreground') : resolveTextClasses(isMatched, isHighlighted),
        // A filled match is already unmistakable, so the dot is kept for the quieter states.
        isMatched && !isFilledMatch && MATCH_DOT_CLASSES,
        className
      )}
      data-matched={isMatched === true || undefined}
      {...props}
    >
      {children}
    </span>
  );
}

export { LotteryNumber };
export type { LotteryNumberProps, LotteryNumberVariant };
